
import React, { useState } from 'react';
import { auth } from '@shared/services/firebase';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
  signOut
} from "firebase/auth";

interface Props {
  onLogin: () => void;
}

const Auth: React.FC<Props> = ({ onLogin }) => {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isRegister) {
        if (password.length < 6) {
          throw { code: 'auth/weak-password', message: 'Password must be at least 6 characters.' };
        }
        
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        
        await updateProfile(userCredential.user, {
          displayName: fullName
        });

        await sendEmailVerification(userCredential.user);
        
        await signOut(auth);
        
        setVerificationSent(true);
      } else {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        
        if (!userCredential.user.emailVerified) {
          setError('Please verify your email before logging in. Check your inbox.');
          await signOut(auth);
          setLoading(false);
          return;
        }
        
        onLogin();
      }
    } catch (err: any) {
      console.error("Auth Error:", err);
      let message = err.message || 'An error occurred during authentication.';
      
      if (err.code === 'auth/email-already-in-use') message = 'This email is already registered.';
      if (err.code === 'auth/wrong-password') message = 'Incorrect password.';
      if (err.code === 'auth/user-not-found') message = 'No account found with this email.';
      if (err.code === 'auth/invalid-email') message = 'Invalid email address format.';
      if (err.code === 'auth/weak-password') message = 'Password is too weak (min 6 characters).';
      if (err.code === 'auth/network-request-failed') message = 'Network error. Please check your connection.';
      
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (verificationSent) {
    return (
      <div className="flex flex-col items-center justify-center p-6 md:p-12 min-h-[600px] bg-[#f8fafc]">
        <div className="w-full max-w-md bg-white p-12 rounded-[3.5rem] border border-slate-100 shadow-2xl text-center animate-in zoom-in duration-500">
          <div className="w-20 h-20 bg-emerald-100 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-xl shadow-emerald-50">
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
          </div>
          <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight">Verify Your Email</h2>
          <p className="text-slate-500 font-medium leading-relaxed mb-10">
            We've sent a verification link to <span className="text-blue-600 font-bold">{email}</span>. Please click the link in your inbox to activate your doctor profile.
          </p>
          <button 
            onClick={() => {
              setVerificationSent(false);
              setIsRegister(false);
              setEmail('');
              setPassword('');
            }}
            className="w-full bg-slate-900 text-white py-5 rounded-2xl font-black shadow-xl hover:bg-blue-600 transition-all flex justify-center items-center gap-2"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center p-6 md:p-12 min-h-[600px] bg-[#f8fafc]">
      <div className="w-20 h-20 bg-blue-600 rounded-[1.75rem] flex items-center justify-center mb-8 shadow-2xl shadow-blue-200 animate-in zoom-in duration-500">
        <span className="text-white font-black text-4xl">M</span>
      </div>
      
      <div className="w-full max-w-md bg-white p-10 rounded-[3rem] border border-slate-100 shadow-2xl shadow-slate-200/40">
        <h2 className="text-3xl font-black text-slate-900 mb-2 text-center tracking-tight">
          {isRegister ? 'Doctor Registration' : 'Doctor Login'}
        </h2>
        <p className="text-slate-500 text-center mb-10 font-medium text-sm">
          {isRegister ? 'Start your journey with real-time cloud analytics.' : 'Access your performance tracks and evidence.'}
        </p>

        {error && (
          <div className="mb-8 p-4 bg-red-50 text-red-600 rounded-2xl text-xs font-bold border border-red-100 flex items-center gap-3 animate-in fade-in slide-in-from-top-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="shrink-0"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <span className="break-words">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {isRegister && (
            <div className="group">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2.5 ml-4 group-focus-within:text-blue-600 transition-colors">Full Name & Title</label>
              <input 
                type="text" 
                required
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                placeholder="Dr. Alexander Wright"
                className="w-full p-5 bg-slate-50 border-2 border-slate-50 rounded-2xl focus:border-blue-500 focus:bg-white outline-none font-bold text-slate-700 transition-all placeholder:text-slate-300"
              />
            </div>
          )}
          
          <div className="group">
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2.5 ml-4 group-focus-within:text-blue-600 transition-colors">NHS / Private Email</label>
            <input 
              type="email" 
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="doctor@nhs.net"
              className="w-full p-5 bg-slate-50 border-2 border-slate-50 rounded-2xl focus:border-blue-500 focus:bg-white outline-none font-bold text-slate-700 transition-all placeholder:text-slate-300"
            />
          </div>

          <div className="group">
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2.5 ml-4 group-focus-within:text-blue-600 transition-colors">Secure Password</label>
            <input 
              type="password" 
              required
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full p-5 bg-slate-50 border-2 border-slate-50 rounded-2xl focus:border-blue-500 focus:bg-white outline-none font-bold text-slate-700 transition-all placeholder:text-slate-300"
            />
          </div>

          <button 
            type="submit"
            disabled={loading}
            className="w-full bg-slate-900 text-white py-5 rounded-2xl font-black shadow-xl shadow-slate-200 hover:bg-blue-600 transition-all active:scale-[0.98] mt-6 flex justify-center items-center group overflow-hidden relative"
          >
            {loading ? (
              <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              <span className="flex items-center gap-2">
                {isRegister ? 'Create Doctor Profile' : 'Sign In to Dashboard'}
                <svg className="group-hover:translate-x-1 transition-transform" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="9 18 15 12 9 6"/></svg>
              </span>
            )}
          </button>
        </form>

        <div className="mt-10 text-center border-t border-slate-100 pt-8">
          <button 
            onClick={() => {
              setIsRegister(!isRegister);
              setError('');
              setVerificationSent(false);
            }}
            className="text-sm font-bold text-slate-400 hover:text-blue-600 transition-colors flex items-center justify-center gap-2 mx-auto"
          >
            {isRegister ? 'Already have a profile? Sign In' : "New to NHSInterview? Create Profile"}
          </button>
        </div>
      </div>
      
      <p className="mt-12 text-[10px] font-black text-slate-300 uppercase tracking-widest">
        Secured by Google Identity Services
      </p>
    </div>
  );
};

export default Auth;
